/**
 * Underlying library changed: @radix-ui/react-select → @base-ui/react/select
 *
 * SelectTrigger:
 *   - OLD: `size?: 'sm' | 'default'` prop (controls height via data-size)
 *   - NEW: no size prop
 *
 * SelectContent → SelectPopup:
 *   - OLD: SelectContent with `position?: 'popper' | 'item-aligned'`
 *   - NEW: SelectPopup with explicit positioning props:
 *          `side`, `sideOffset`, `align`, `alignOffset`, `alignItemWithTrigger`, `anchor`
 *   - SelectContent is re-exported as alias for SelectPopup for compatibility
 *
 * SelectScrollUpButton / SelectScrollDownButton:
 *   - OLD: exported for manual composition
 *   - NEW: built into SelectPopup; not exported separately
 *
 * SelectGroupLabel: NEW — no equivalent in old API
 */
/** biome-ignore-all lint/style/useComponentExportOnlyModules: storybook export pattern */
'use client'

import { Select as SelectPrimitive } from '@base-ui/react/select'
import { CheckIcon, ChevronDownIcon, ChevronUpIcon, CircleIcon } from './icon'
import * as React from 'react'
import { use } from 'react'
import { cn, extractTextFromChildren } from '../lib/utils'
import { isTest } from '@w/utils'

const SelectMultipleContext = React.createContext(false)
/** Whether this select is minimal — set on the root, read by trigger (bare) and popup (dense). */
const SelectMinimalContext = React.createContext(false)

export function Select<
  Value = string,
  Multiple extends boolean | undefined = false,
>({
  multiple,
  minimal,
  ...props
}: SelectPrimitive.Root.Props<Value, Multiple> & {
  /** Minimal select: bare 24px trigger + dense popup. */
  minimal?: boolean
}) {
  return (
    <SelectMultipleContext.Provider value={(multiple as boolean) ?? false}>
      <SelectMinimalContext.Provider value={minimal ?? false}>
        <SelectPrimitive.Root multiple={multiple} {...props} />
      </SelectMinimalContext.Provider>
    </SelectMultipleContext.Provider>
  )
}

export function SelectTrigger({
  className,
  children,
  minimal: minimalProp,
  ...props
}: SelectPrimitive.Trigger.Props & {
  /** Bare trigger, 24px tall. Prefer setting `minimal` on the `Select` root so the popup is dense too. */
  minimal?: boolean
}): React.ReactElement {
  const minimal = minimalProp ?? use(SelectMinimalContext)
  return (
    <SelectPrimitive.Trigger
      className={cn(
        minimal
          ? "text-on-surface aria-invalid:text-error text-label-medium relative inline-flex min-h-6 cursor-pointer items-center justify-between gap-2 border-none bg-transparent text-left outline-none select-none data-disabled:cursor-not-allowed data-disabled:opacity-64 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='opacity-'])]:opacity-80 [&_svg:not([class*='size-'])]:size-5"
          : "border-border text-on-surface ring-primary/80 focus-visible:border-outline aria-invalid:border-error focus-visible:aria-invalid:border-error focus-visible:aria-invalid:ring-error text-label-medium relative inline-flex min-h-8 min-w-24 cursor-pointer items-center justify-between gap-2 rounded-lg border bg-transparent py-3 pr-3 pl-4 text-left outline-none select-none focus-visible:ring-[1px] data-disabled:cursor-not-allowed data-disabled:opacity-64 pointer-coarse:after:absolute pointer-coarse:after:size-full pointer-coarse:after:min-h-11 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='opacity-'])]:opacity-80 [&_svg:not([class*='size-'])]:size-5",
        className,
      )}
      data-slot="select-trigger"
      {...props}
    >
      {children}
      <SelectPrimitive.Icon data-slot="select-icon">
        <ChevronDownIcon className="in-aria-invalid:text-error" />
      </SelectPrimitive.Icon>
    </SelectPrimitive.Trigger>
  )
}

export function SelectValue({
  className,
  ...props
}: SelectPrimitive.Value.Props): React.ReactElement {
  return (
    <SelectPrimitive.Value
      className={cn(
        'data-placeholder:text-on-surface-secondary in-aria-invalid:data-placeholder:text-error/60 flex-1 truncate',
        className,
      )}
      data-slot="select-value"
      {...props}
    />
  )
}

export function SelectPopup({
  className,
  children,
  side = 'bottom',
  sideOffset = 4,
  align = 'center',
  alignOffset = 0,
  alignItemWithTrigger = true,
  anchor,
  small,
  ...props
}: SelectPrimitive.Popup.Props & {
  side?: SelectPrimitive.Positioner.Props['side']
  sideOffset?: SelectPrimitive.Positioner.Props['sideOffset']
  align?: SelectPrimitive.Positioner.Props['align']
  alignOffset?: SelectPrimitive.Positioner.Props['alignOffset']
  alignItemWithTrigger?: SelectPrimitive.Positioner.Props['alignItemWithTrigger']
  anchor?: SelectPrimitive.Positioner.Props['anchor']
  /** Dense rows: tighter item padding, smaller text. Defaults to true for minimal selects. */
  small?: boolean
}): React.ReactElement {
  const minimal = use(SelectMinimalContext)
  const dense = small ?? minimal
  return (
    <SelectPrimitive.Portal>
      <SelectPrimitive.Positioner
        align={align}
        alignItemWithTrigger={alignItemWithTrigger}
        alignOffset={alignOffset}
        anchor={anchor}
        className="z-50 select-none"
        data-slot="select-positioner"
        side={side}
        sideOffset={sideOffset}
      >
        <SelectPrimitive.Popup
          className="text-on-surface origin-(--transform-origin) transition-[scale,opacity] duration-150 ease-out outline-none data-ending-style:scale-95 data-ending-style:opacity-0 data-starting-style:scale-95 data-starting-style:opacity-0"
          data-slot="select-popup"
          {...props}
        >
          <SelectPrimitive.ScrollUpArrow
            className="before:from-surface-3 top-0 z-50 flex h-6 w-full items-center justify-center before:pointer-events-none before:absolute before:inset-x-0 before:top-0 before:h-[200%] before:rounded-t-[12px] before:bg-linear-to-b before:from-50%"
            data-slot="select-scroll-up-arrow"
          >
            <ChevronUpIcon className="relative size-5" />
          </SelectPrimitive.ScrollUpArrow>
          <div
            className="bg-surface-3 relative h-full w-(--anchor-width) rounded-[12px] not-dark:bg-clip-padding"
            data-small={dense ? '' : undefined}
          >
            <SelectPrimitive.List
              className={cn(
                'max-h-(--available-height) overflow-y-auto p-1',
                className,
              )}
              data-slot="select-list"
            >
              {children}
            </SelectPrimitive.List>
          </div>
          <SelectPrimitive.ScrollDownArrow
            className="before:from-surface-3 bottom-0 z-50 flex h-6 w-full items-center justify-center before:pointer-events-none before:absolute before:inset-x-0 before:bottom-0 before:h-[200%] before:rounded-b-[12px] before:bg-linear-to-t before:from-50%"
            data-slot="select-scroll-down-arrow"
          >
            <ChevronDownIcon className="relative size-5" />
          </SelectPrimitive.ScrollDownArrow>
        </SelectPrimitive.Popup>
      </SelectPrimitive.Positioner>
    </SelectPrimitive.Portal>
  )
}

export function SelectItem({
  className,
  children,
  ...props
}: SelectPrimitive.Item.Props): React.ReactElement {
  const isMultiple = use(SelectMultipleContext)

  let testId = (props as Record<string, unknown>)['data-testid'] as
    | string
    | undefined
  if (!testId && isTest) testId = extractTextFromChildren(children)

  return (
    <SelectPrimitive.Item
      className={cn(
        "data-highlighted:bg-primary/8 text-label-medium flex min-h-8 cursor-pointer items-center justify-between gap-2 rounded-md p-3 outline-none data-disabled:cursor-not-allowed data-disabled:opacity-64 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-5",
        "in-data-[small]:text-label-small in-data-[small]:min-h-6 in-data-[small]:[&_svg:not([class*='size-'])]:size-4",
        className,
      )}
      data-slot="select-item"
      {...props}
      data-testid={testId}
    >
      <SelectPrimitive.ItemText className="flex min-w-0 items-center gap-2 truncate">
        {children}
      </SelectPrimitive.ItemText>
      <SelectPrimitive.ItemIndicator>
        {isMultiple ? (
          <CheckIcon />
        ) : (
          <CircleIcon className="size-2 fill-current" />
        )}
      </SelectPrimitive.ItemIndicator>
    </SelectPrimitive.Item>
  )
}

export function SelectSeparator({
  className,
  ...props
}: SelectPrimitive.Separator.Props): React.ReactElement {
  return (
    <SelectPrimitive.Separator
      className={cn('bg-separator mx-2 my-1 h-px', className)}
      data-slot="select-separator"
      {...props}
    />
  )
}

export function SelectGroup(
  props: SelectPrimitive.Group.Props,
): React.ReactElement {
  return <SelectPrimitive.Group data-slot="select-group" {...props} />
}

export function SelectLabel({
  className,
  ...props
}: SelectPrimitive.Label.Props): React.ReactElement {
  return (
    <SelectPrimitive.Label
      className={cn(
        'text-on-surface inline-flex cursor-default items-center gap-2 text-base/4.5 font-medium',
        className,
      )}
      data-slot="select-label"
      {...props}
    />
  )
}

export function SelectGroupLabel(
  props: SelectPrimitive.GroupLabel.Props,
): React.ReactElement {
  return (
    <SelectPrimitive.GroupLabel
      className="text-on-surface-secondary text-label-small px-2 py-1.5 text-center font-medium"
      data-slot="select-group-label"
      {...props}
    />
  )
}

export { SelectPrimitive, SelectPopup as SelectContent }
